import dayjs from "dayjs";
import nodeAPI from "../../service/ApiServiceNode";
import { API_CHECKLIST_TASK } from "../../constants";
import { resolveDate } from "../../components/Tasks/VoiceTaskCreate/date-resolver";
import { fuzzyMatchUsers } from "../../components/Tasks/VoiceTaskCreate/fuzzy-match";
import { DEFAULT_TASK_PRIORITY, TASK_PRIORITY_VALUES, priorityRequirementLabel } from "../../components/Tasks/task-priority-config";

const VALID_FIELDS = ["name", "description", "priority", "type", "dueDate"];
const VALID_TYPES = ["standard", "approval"];

function setField({ field, value }, ctx) {
    if (!VALID_FIELDS.includes(field)) return { ok: false, detail: `unknown field: ${field}` };
    if (field === "priority") {
        const upper = String(value || "").toUpperCase();
        if (!TASK_PRIORITY_VALUES.includes(upper)) return { ok: false, detail: `priority must be ${priorityRequirementLabel()}` };
        ctx.actions.patchTaskDraft({ priority: upper });
        return { ok: true, detail: `priority set to ${upper}` };
    }
    if (field === "type") {
        const lower = String(value || "").toLowerCase();
        if (!VALID_TYPES.includes(lower)) return { ok: false, detail: "type must be standard or approval" };
        ctx.actions.patchTaskDraft({ type: lower });
        return { ok: true, detail: `type set to ${lower}` };
    }
    if (field === "dueDate") {
        const iso = resolveDate(value);
        if (!iso) return { ok: false, detail: `could not parse date "${value}"` };
        ctx.actions.patchTaskDraft({ dueDate: iso, dueDateLabel: String(value).trim() });
        return { ok: true, detail: `due ${iso}` };
    }
    ctx.actions.patchTaskDraft({ [field]: String(value || "").trim() });
    return { ok: true, detail: `${field} set` };
}

function clearField({ field }, ctx) {
    const map = {
        name: { name: null },
        description: { description: null },
        priority: { priority: DEFAULT_TASK_PRIORITY },
        type: { type: "standard" },
        dueDate: { dueDate: null, dueDateLabel: null },
        assignee: { assigneeId: null, assigneeLabel: null },
        verifier: { verifierId: null, verifierLabel: null },
        project: { projectId: null, projectLabel: null }
    };
    if (!map[field]) return { ok: false, detail: `unknown field: ${field}` };
    ctx.actions.patchTaskDraft(map[field]);
    ctx.actions.clearTaskPendingOptions(field);
    return { ok: true };
}

async function lookupPerson({ query, role }, ctx) {
    if (!["assignee", "verifier"].includes(role)) return { ok: false, detail: "role must be assignee or verifier" };
    if (!query || !String(query).trim()) return { ok: false, detail: "query is required" };
    const res = await nodeAPI.get("/user/search", { params: { q: query, limit: 8 } });
    let users = res?.data?.data?.users || res?.data?.users || [];
    let usedFuzzy = false;

    if (users.length === 0) {
        const activeUsers = ctx.store.getState().activeUsers;
        const fuzzy = fuzzyMatchUsers(query, activeUsers, { limit: 5 });
        if (fuzzy.length > 0) {
            users = fuzzy;
            usedFuzzy = true;
        }
    }

    if (users.length === 0) {
        ctx.actions.clearTaskPendingOptions(role);
        return { ok: true, count: 0, candidates: [], fuzzy: false };
    }

    const candidates = users.map((u) => ({
        id: u.id,
        label: u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
        sublabel: [u.office, u.department].filter(Boolean).join(" · ") || u.email,
        gender: u.gender || null,
        avatar: u.profilePicture || null,
        email: u.email || null
    }));

    if (users.length === 1 || (usedFuzzy && users[0].score >= 8 && (!users[1] || users[1].score < users[0].score - 2))) {
        const picked = candidates[0];
        ctx.actions.patchTaskDraft(
            role === "assignee"
                ? { assigneeId: picked.id, assigneeLabel: picked.label }
                : { verifierId: picked.id, verifierLabel: picked.label }
        );
        ctx.actions.clearTaskPendingOptions(role);
        return { ok: true, count: 1, picked, candidates, fuzzy: usedFuzzy };
    }

    ctx.actions.setTaskPendingOptions(role, candidates);
    return { ok: true, count: candidates.length, candidates, fuzzy: usedFuzzy };
}

async function lookupProject({ query }, ctx) {
    if (!query || !String(query).trim()) return { ok: false, detail: "query is required" };
    const res = await nodeAPI.get("/project/dropdown", { params: { search: query, limit: 8 } });
    const projects = res?.data?.data?.projects || res?.data?.projects || res?.data?.data || [];
    const list = Array.isArray(projects) ? projects : [];
    if (list.length === 0) {
        ctx.actions.clearTaskPendingOptions("project");
        return { ok: true, count: 0, candidates: [] };
    }
    const candidates = list.map((p) => ({
        id: p.id,
        label: p.name || p.label || `Project ${p.id}`,
        sublabel: p.client_name || p.code || ""
    }));
    if (list.length === 1) {
        ctx.actions.patchTaskDraft({ projectId: candidates[0].id, projectLabel: candidates[0].label });
        ctx.actions.clearTaskPendingOptions("project");
        return { ok: true, count: 1, picked: candidates[0], candidates };
    }
    ctx.actions.setTaskPendingOptions("project", candidates);
    return { ok: true, count: candidates.length, candidates };
}

function readBackDraft(_args, ctx) {
    const state = ctx.store.getState();
    return { ok: true, draft: state.taskDraft, pendingOptions: state.taskPendingOptions };
}

async function commitTask(_args, ctx) {
    const state = ctx.store.getState();
    const draft = state.taskDraft;
    const missing = [];
    if (!draft.name || !draft.name.trim()) missing.push("name");
    if (!draft.dueDate) missing.push("dueDate");
    if (missing.length) return { ok: false, detail: `missing required fields: ${missing.join(", ")}` };
    if (state.saving || state.saved) return { ok: false, detail: "already saving or saved" };

    ctx.actions.setSaving(true);
    const payload = {
        name: draft.name,
        description: draft.description || null,
        priority: draft.priority,
        dueDate: dayjs(draft.dueDate).toISOString(),
        projectId: draft.projectId || null,
        taskType: draft.type,
        assigneeId: draft.assigneeId || null,
        verifierId: draft.verifierId || null,
        source: "voice"
    };

    try {
        const res = await nodeAPI.post(`${API_CHECKLIST_TASK}/standalone`, payload);
        const created = res?.data?.data || res?.data || null;
        ctx.actions.setSaving(false);
        ctx.actions.setSaved(true);
        ctx.onSaved?.(created);
        return { ok: true, taskId: created?.id || null };
    } catch (err) {
        ctx.actions.setSaving(false);
        return {
            ok: false,
            detail: err?.response?.data?.data?.message || err?.response?.data?.message || err?.message || "save failed"
        };
    }
}

function cancel(_args, ctx) {
    ctx.onCancelled?.();
    return { ok: true };
}

export const taskTools = [
    {
        key: "task.set_field",
        schema: {
            type: "function",
            description: "Set a scalar field on the staged task. For dueDate pass the user's phrase verbatim.",
            parameters: {
                type: "object",
                properties: {
                    field: { type: "string", enum: VALID_FIELDS },
                    value: { type: "string" }
                },
                required: ["field", "value"]
            }
        },
        executor: setField
    },
    {
        key: "task.lookup_person",
        schema: {
            type: "function",
            description: "Search active users by name or email and stage the result on assignee or verifier.",
            parameters: {
                type: "object",
                properties: {
                    query: { type: "string" },
                    role: { type: "string", enum: ["assignee", "verifier"] }
                },
                required: ["query", "role"]
            }
        },
        executor: lookupPerson
    },
    {
        key: "task.lookup_project",
        schema: {
            type: "function",
            description: "Search projects by name and stage the best match on the task.",
            parameters: {
                type: "object",
                properties: { query: { type: "string" } },
                required: ["query"]
            }
        },
        executor: lookupProject
    },
    {
        key: "task.clear_field",
        schema: {
            type: "function",
            description: "Clear a staged task field.",
            parameters: {
                type: "object",
                properties: {
                    field: { type: "string", enum: [...VALID_FIELDS, "assignee", "verifier", "project"] }
                },
                required: ["field"]
            }
        },
        executor: clearField
    },
    {
        key: "task.read_back_draft",
        schema: { type: "function", description: "Return the staged task draft.", parameters: { type: "object", properties: {} } },
        executor: readBackDraft
    },
    {
        key: "task.commit",
        schema: { type: "function", description: "Create the staged task after explicit user confirmation.", parameters: { type: "object", properties: {} } },
        executor: commitTask
    },
    {
        key: "task.cancel",
        schema: { type: "function", description: "Discard the draft and close the modal.", parameters: { type: "object", properties: {} } },
        executor: cancel
    }
];
